import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { CheckCircle } from 'lucide-react';

const CheckoutPage = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: ''
  });
  const [errors, setErrors] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'Name is required';
    if (formData.customer_email.trim() && !/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = 'Email is invalid';
    }
    if (!formData.customer_phone.trim()) {
      newErrors.customer_phone = 'Phone is required';
    } else if (!/^\d{10}$/.test(formData.customer_phone.replace(/\D/g, ''))) {
      newErrors.customer_phone = 'Phone must be 10 digits';
    }
    if (!formData.shipping_address.trim()) newErrors.shipping_address = 'Address is required';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ...formData,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          price: item.price,
          final_price: item.final_price,
          quantity: item.quantity
        })),
        total_amount: getCartTotal()
      };

      const response = await axios.post(`${BACKEND_URL}/api/orders`, orderData);
      setOrderNumber(response.data.order_number);
      setOrderComplete(true);
      clearCart();
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0 && !orderComplete) {
    navigate('/cart');
    return null;
  }

  const originalTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const discountTotal = originalTotal - getCartTotal();

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="order-success">
        <div className="max-w-2xl mx-auto px-6 text-center space-y-8">
          <CheckCircle size={80} className="text-[#34C759] mx-auto" />
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">
              Order Placed Successfully!
            </h1>
            <p className="text-xl text-[#D4AF37]" data-testid="order-number-display">
              Order #{orderNumber}
            </p>
            <p className="text-[#A3A3A3] leading-relaxed">
              Thank you for your purchase. We'll send you a confirmation email shortly with your order details.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
            <button
              onClick={() => navigate('/products')}
              className="px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300"
              data-testid="continue-shopping-success-button"
            >
              Continue Shopping
            </button>
            <button
              onClick={() => navigate('/')}
              className="px-10 py-4 bg-transparent text-white border border-white/20 hover:border-white font-medium tracking-wide uppercase text-sm transition-all duration-300"
              data-testid="back-to-home-button"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-white mb-12" data-testid="checkout-page-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-[#141414] border border-white/10 p-8 space-y-6">
                <h2 className="text-2xl font-light tracking-tighter text-white">Shipping Information</h2>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Full Name</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-3 outline-none transition-colors"
                      data-testid="checkout-name-input"
                    />
                    {errors.customer_name && <p className="text-[#FF3B30] text-sm mt-1">{errors.customer_name}</p>}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Email</label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-3 outline-none transition-colors"
                      data-testid="checkout-email-input"
                    />
                    {errors.customer_email && <p className="text-[#FF3B30] text-sm mt-1">{errors.customer_email}</p>}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Phone</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white px-0 py-3 outline-none transition-colors"
                      data-testid="checkout-phone-input"
                    />
                    {errors.customer_phone && <p className="text-[#FF3B30] text-sm mt-1">{errors.customer_phone}</p>}
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Shipping Address</label>
                    <textarea
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleChange}
                      rows="4"
                      className="w-full bg-transparent border border-white/20 focus:border-[#D4AF37] text-white px-4 py-3 outline-none transition-colors resize-none"
                      data-testid="checkout-address-input"
                    />
                    {errors.shipping_address && <p className="text-[#FF3B30] text-sm mt-1">{errors.shipping_address}</p>}
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-10 py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="place-order-button"
              >
                {loading ? 'Placing Order...' : 'Place Order'}
              </button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-[#141414] border border-white/10 p-8 space-y-6 sticky top-24">
              <h2 className="text-2xl font-light tracking-tighter text-white">Order Summary</h2>

              <div className="space-y-4">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm" data-testid={`checkout-item-${item.id}`}>
                    <span className="text-[#A3A3A3]">
                      {item.name} x {item.quantity}
                    </span>
                    <span className="text-white">
                      ₹{(item.final_price * item.quantity).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-3 py-6 border-y border-white/10">
                <div className="flex justify-between text-[#A3A3A3]">
                  <span>Subtotal</span>
                  <span>₹{getCartTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-[#A3A3A3]">
                  <span>Shipping</span>
                  <span>FREE</span>
                </div>
                {discountTotal > 0 && (
                  <div className="flex justify-between text-[#34C759]">
                    <span>Discount</span>
                    <span>-&#8377;{discountTotal.toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between text-xl">
                <span className="text-white">Total</span>
                <span className="text-[#D4AF37] font-light" data-testid="checkout-total">
                  ₹{getCartTotal().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
